import ABIS from './contractabis.js'
import { kulaApiFetch } from './kulaApiFetch.js'
import { ethers } from 'ethers'

function _getContract({ daoAddress, abiMethods }) {
  const provider = new ethers.providers.Web3Provider(window.ethereum)

  provider.getNetwork().catch((err) => {
    console.error('Network detection error:', err)
  })

  const signer = provider.getSigner()

  return new ethers.Contract(daoAddress, abiMethods, signer)
}

/** @param {RegionalDaoConfig} body */
export async function createRegionalDao(body) {
  return await kulaApiFetch('/v1/contracts/daos', {
    method: 'POST',
    body,
  })
}

/** @returns {Promise<Dao[]>} */
export async function getRegionalDaos() {
  return await kulaApiFetch('/v1/contracts/daos', {
    method: 'GET',
  }).then((response) => response.json())
}

/**
 * @param {{daoAddress: string, recipientAddresses: string, amount: number}} body
 * */
export async function distributeDaoToken(body) {
  const contract = _getContract({
    daoAddress: body.daoAddress,
    abiMethods: ABIS.REGIONAL_DAO_TOKEN.methods,
  })
  const decimals = 18
  const wholeTokenAmount = body.amount
  const gasLimit = 1000000
  const sendAmount = ethers.BigNumber.from(wholeTokenAmount).mul(
    ethers.BigNumber.from(10).pow(decimals)
  )

  try {
    await contract.transfer(body.recipientAddresses, sendAmount.toString(), {
      gasLimit,
    })
  } catch (error) {
    console.error('Error distributing DAO token:', error)
    throw error
  }
}

/**
 * @param {string} deployId
 * */
export async function getRegionalDaoStatus(deployId) {
  return kulaApiFetch(`/v1/contracts/daos/deploy/status?deployid=${deployId}`, {
    method: 'GET',
  })
}

export async function addDaoExecutive(daoAddress, address) {
  if (!daoAddress || !address) {
    throw new Error('Missing required addresses')
  }

  const addressRegex = /^0x[a-fA-F0-9]{40}$/
  if (!addressRegex.test(daoAddress)) {
    throw new Error(`Invalid daoAddress format: ${daoAddress}`)
  }
  if (!addressRegex.test(address)) {
    throw new Error(`Invalid address format: ${address}`)
  }

  try {
    const contract = _getContract({
      daoAddress,
      abiMethods: ABIS.MULTI_SIG.methods,
    })
    const tx = await contract.addExecutive(address, {
      gasLimit: 1000000,
    })
  } catch (error) {
    console.error('Error adding DAO executive:', error)
    throw error
  }
}

export async function removeDaoExecutive(daoAddress, address) {
  if (!daoAddress || !address) {
    throw new Error('Missing required addresses')
  }

  const addressRegex = /^0x[a-fA-F0-9]{40}$/
  if (!addressRegex.test(daoAddress)) {
    throw new Error(`Invalid daoAddress format: ${daoAddress}`)
  }
  if (!addressRegex.test(address)) {
    throw new Error(`Invalid address format: ${address}`)
  }

  try {
    const contract = _getContract({
      daoAddress,
      abiMethods: ABIS.MULTI_SIG.methods,
    })
    const tx = await contract.removeExecutive(address, {
      gasLimit: 1000000,
    })
    return tx
  } catch (error) {
    console.error('Error removing DAO executive:', error)
    throw error
  }
}

export async function getMultiSigOwners(daoAddress) {
  const contract = _getContract({
    daoAddress,
    abiMethods: ABIS.MULTI_SIG.methods,
  })
  const executives = await contract.getExecutives()
  return executives
}

export async function updateQuorum(daoAddress, quorum) {
  const contract = _getContract({
    daoAddress,
    abiMethods: ABIS.REGIONAL_DAO_GOVERNOR.methods,
  })
  const tx = await contract.updateQuorumNumerator(quorum, {
    gasLimit: 1000000,
  })
  return tx
}

export async function getQuorum(daoAddress) {
  const contract = _getContract({
    daoAddress,
    abiMethods: ABIS.REGIONAL_DAO_GOVERNOR.methods,
  })
  const provider = new ethers.providers.Web3Provider(window.ethereum)

  const block = await provider.getBlock('latest')
  const timestamp = block.timestamp - 1

  const quorumNumerator = await contract.quorumNumerator(timestamp)
  const tokenAddress = await contract.token()

  const tokenContract = _getContract({
    daoAddress: tokenAddress,
    abiMethods: ABIS.REGIONAL_DAO_TOKEN.methods,
  })

  const totalSupply = await tokenContract.getPastTotalSupply(timestamp)
  const readableTotalSupply = ethers.utils.formatEther(totalSupply)

  const sqrtTotalSupply = Math.sqrt(Number(readableTotalSupply))

  const votesNeeded = (sqrtTotalSupply * Number(quorumNumerator)) / 100

  return votesNeeded
}

export async function getTotalSupply(daoAddress) {
  const contract = _getContract({
    daoAddress,
    abiMethods: ABIS.REGIONAL_DAO_TOKEN.methods,
  })
  const totalSupply = await contract.totalSupply()
  const readableTotalSupply = ethers.utils.formatEther(totalSupply)
  return readableTotalSupply
}

export async function checkTimelockReady(daoAddress) {
  console.log('daoAddress', daoAddress)
  const contract = _getContract({
    daoAddress: daoAddress,
    abiMethods: ABIS.TIMELOCK.methods,
  })
  console.log('contract', contract)
  const minDelay = await contract.getMinDelay()
  const minDelaySeconds = minDelay.toNumber
    ? minDelay.toNumber()
    : Number(minDelay)
  const minDelayMinutes = minDelaySeconds / 60
  const minDelayHours = minDelaySeconds / 3600
  console.log('minDelay (seconds):', minDelaySeconds)
  console.log('minDelay (minutes):', minDelayMinutes)
  console.log('minDelay (hours):', minDelayHours)
  return minDelaySeconds
}

/**
 * Register the RegionalDaoGovernor as a DAO in the Registry
 * @param {string} registryAddress - The Registry contract address
 * @param {string} regionalDaoGovernorAddress - The RegionalDaoGovernor contract address
 */
export async function registerGovernorAsDao(
  registryAddress,
  regionalDaoGovernorAddress
) {
  const contract = _getContract({
    daoAddress: registryAddress,
    abiMethods: ABIS.REGISTRY.methods,
  })

  // Calculate the DAO_ROLE hash
  const DAO_ROLE = ethers.utils.keccak256(ethers.utils.toUtf8Bytes('DAO_ROLE'))

  try {
    // Check if role is already granted
    const hasRole = await contract.hasRole(DAO_ROLE, regionalDaoGovernorAddress)

    if (hasRole) {
      console.log('✅ DAO_ROLE already granted to RegionalDaoGovernor')
      return { success: true, message: 'DAO_ROLE already granted' }
    }

    const tx = await contract.grantRole(DAO_ROLE, regionalDaoGovernorAddress, {
      gasLimit: 1000000,
    })
    const receipt = await tx.wait()
    console.log(
      '✅ Successfully granted DAO_ROLE to RegionalDaoGovernor:',
      receipt
    )
    return { success: true, receipt }
  } catch (error) {
    console.error('❌ Error granting DAO_ROLE:', error)
    throw error
  }
}

/**
 * Add the MultiSig wallet mapping to the Registry
 * @param {string} registryAddress - The Registry contract address
 * @param {string} regionalDaoGovernorAddress - The RegionalDaoGovernor contract address
 * @param {string} multiSigWalletAddress - The MultiSig wallet contract address
 */
export async function addDaoMultiSigWalletMapping(
  registryAddress,
  regionalDaoGovernorAddress,
  multiSigWalletAddress
) {
  const contract = _getContract({
    daoAddress: registryAddress,
    abiMethods: ABIS.REGISTRY.methods,
  })

  try {
    // Check if mapping already exists
    const existingMapping = await contract.getDaoMultiSigWallet(
      regionalDaoGovernorAddress
    )

    if (existingMapping === multiSigWalletAddress) {
      console.log('✅ MultiSig wallet mapping already exists')
      return {
        success: true,
        message: 'MultiSig wallet mapping already exists',
      }
    }

    const tx = await contract.addDaoMultiSigWallet(
      regionalDaoGovernorAddress,
      multiSigWalletAddress,
      {
        gasLimit: 1000000,
      }
    )
    const receipt = await tx.wait()
    console.log('✅ Successfully added MultiSig wallet mapping:', receipt)
    return { success: true, receipt }
  } catch (error) {
    console.error('❌ Error adding MultiSig wallet mapping:', error)
    throw error
  }
}
